import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, Camera, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage, LangToggle } from "@/context/LanguageContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const inputCls =
  "w-full bg-obsidian border border-white/15 px-5 py-4 text-white placeholder:text-white/30 focus:border-volt focus:outline-none transition-colors duration-300";
const labelCls = "block font-jetbrains text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api
      .get("/profile")
      .then((r) => setForm(r.data))
      .catch(() => toast.error(t("dash.loadErr")));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/profile", form);
      setForm(data);
      toast.success(t("dash.saved"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/profile/photo", body);
      setForm((f) => ({ ...f, photo_path: data.photo_path }));
      toast.success(t("dash.photoOk"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const sendProfile = async () => {
    setSending(true);
    try {
      await api.post("/profile/enquire");
      toast.success(t("dash.sentOk"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const signOut = async () => {
    await logout();
    navigate("/");
  };

  if (!form) return <div className="min-h-screen bg-obsidian" data-testid="dashboard-loading" />;

  return (
    <div className="min-h-screen bg-obsidian text-[#F5F5F5] font-satoshi px-6 md:px-12 py-10" data-testid="dashboard-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-volt transition-colors text-sm uppercase tracking-widest" data-testid="dashboard-back-link">
            <ArrowLeft size={16} /> {t("dash.back")}
          </Link>
          <div className="flex items-center gap-6">
            <LangToggle />
            <button onClick={signOut} data-testid="dashboard-logout-button" className="inline-flex items-center gap-2 text-white/50 hover:text-crimson transition-colors text-sm uppercase tracking-widest">
              <LogOut size={16} /> {t("dash.signout")}
            </button>
          </div>
        </div>

        <p className="font-jetbrains text-xs uppercase tracking-[0.35em] text-volt mb-3" data-testid="dashboard-email">{user.email}</p>
        <h1 className="font-cabinet font-black uppercase text-4xl md:text-5xl tracking-tight mb-12">
          {t("dash.h1a")}<span className="text-volt">{t("dash.h1b")}</span>
        </h1>

        <div className="flex items-center gap-6 mb-10" data-testid="photo-section">
          <div className="w-28 h-28 border border-white/15 bg-surface overflow-hidden shrink-0">
            {form.photo_path ? (
              <img src={`${BACKEND}/api/files/${form.photo_path}`} alt="Your profile" className="w-full h-full object-cover" data-testid="profile-photo-preview" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/20" data-testid="profile-photo-empty">
                <Camera size={28} />
              </div>
            )}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="profile-photo-input" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="profile-photo-button"
              className="rounded-full border border-white/25 text-white/80 font-cabinet font-bold uppercase tracking-wide px-6 py-3 text-sm hover:border-volt hover:text-volt transition-colors duration-300 disabled:opacity-50"
            >
              {uploading ? t("dash.uploading") : form.photo_path ? t("dash.changePhoto") : t("dash.addPhoto")}
            </button>
            <p className="font-jetbrains text-[10px] uppercase tracking-[0.25em] text-white/30 mt-3">{t("dash.photoHint")}</p>
          </div>
        </div>

        <form onSubmit={save} className="bg-surface border border-white/10 p-8 md:p-12 space-y-6" data-testid="profile-form">
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="profile-name" className={labelCls}>{t("dash.name")}</label>
              <input id="profile-name" data-testid="profile-name-input" value={form.name} onChange={set("name")} placeholder={t("auth.namePh")} className={inputCls} />
            </div>
            <div>
              <label htmlFor="profile-location" className={labelCls}>{t("dash.location")}</label>
              <input id="profile-location" data-testid="profile-location-input" value={form.location} onChange={set("location")} placeholder={t("dash.locationPh")} className={inputCls} />
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            <div>
              <label htmlFor="profile-gradyear" className={labelCls}>{t("dash.gradyear")}</label>
              <input id="profile-gradyear" data-testid="profile-gradyear-input" value={form.grad_year} onChange={set("grad_year")} placeholder="2027" className={inputCls} />
            </div>
            <div>
              <label htmlFor="profile-height" className={labelCls}>{t("dash.height")}</label>
              <input id="profile-height" data-testid="profile-height-input" value={form.height} onChange={set("height")} placeholder={t("dash.heightPh")} className={inputCls} />
            </div>
            <div>
              <label htmlFor="profile-gpa" className={labelCls}>{t("dash.gpa")}</label>
              <input id="profile-gpa" data-testid="profile-gpa-input" value={form.gpa} onChange={set("gpa")} placeholder="3.5" className={inputCls} />
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            <div>
              <label htmlFor="profile-position" className={labelCls}>{t("dash.position")}</label>
              <select id="profile-position" data-testid="profile-position-select" value={form.position} onChange={set("position")} className={inputCls}>
                <option value="">{t("dash.select")}</option>
                <option value="Goalkeeper">{t("dash.posGk")}</option>
                <option value="Defender">{t("dash.posDf")}</option>
                <option value="Midfielder">{t("dash.posMf")}</option>
                <option value="Forward / Winger">{t("dash.posFw")}</option>
                <option value="Other">{t("dash.posOther")}</option>
              </select>
            </div>
            <div>
              <label htmlFor="profile-foot" className={labelCls}>{t("dash.foot")}</label>
              <select id="profile-foot" data-testid="profile-foot-select" value={form.preferred_foot} onChange={set("preferred_foot")} className={inputCls}>
                <option value="">{t("dash.select")}</option>
                <option value="Right">{t("dash.footR")}</option>
                <option value="Left">{t("dash.footL")}</option>
                <option value="Both">{t("dash.footB")}</option>
              </select>
            </div>
            <div>
              <label htmlFor="profile-club" className={labelCls}>{t("dash.club")}</label>
              <input id="profile-club" data-testid="profile-club-input" value={form.current_club} onChange={set("current_club")} placeholder={t("dash.clubPh")} className={inputCls} />
            </div>
          </div>

          <div>
            <label htmlFor="profile-highlight" className={labelCls}>{t("dash.highlight")}</label>
            <input id="profile-highlight" data-testid="profile-highlight-input" type="url" value={form.highlight_url} onChange={set("highlight_url")} placeholder="https://youtube.com/…" className={inputCls} />
          </div>

          <div>
            <label htmlFor="profile-bio" className={labelCls}>{t("dash.bio")}</label>
            <textarea id="profile-bio" data-testid="profile-bio-input" rows={5} value={form.bio} onChange={set("bio")} placeholder={t("dash.bioPh")} className={`${inputCls} resize-none`} />
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="profile-save-button"
            className="w-full rounded-full bg-volt text-obsidian font-cabinet font-bold uppercase tracking-wide px-8 py-4 hover:bg-white transition-colors duration-300 disabled:opacity-50"
          >
            {busy ? t("dash.saving") : t("dash.save")}
          </button>
        </form>

        <div className="mt-6 bg-volt/5 border border-volt/30 p-8 md:p-10 flex flex-col sm:flex-row sm:items-center gap-6 justify-between" data-testid="send-profile-card">
          <div>
            <h2 className="font-cabinet font-bold uppercase text-xl tracking-tight">{t("dash.sendH2")}</h2>
            <p className="text-white/50 text-sm mt-2 max-w-sm">
              {t("dash.sendCopy")}
            </p>
          </div>
          <button
            onClick={sendProfile}
            disabled={sending}
            data-testid="send-profile-button"
            className="shrink-0 rounded-full bg-volt text-obsidian font-cabinet font-bold uppercase tracking-wide px-7 py-4 flex items-center gap-2 hover:bg-white transition-colors duration-300 disabled:opacity-50"
          >
            <Send size={16} />
            {sending ? t("dash.sending") : t("dash.sendBtn")}
          </button>
        </div>
      </div>
    </div>
  );
}
